import { useState } from 'react'
import { Card, Table, Badge, Alert, Spinner, Form, Row, Col } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'

const BudgetAlerts = ({ budget }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('')

  // Obtener alertas del presupuesto
  const { data: alertsData, isLoading, error } = useQuery(
    ['budget-alerts', budget.id, selectedPeriod],
    async () => {
      const params = new URLSearchParams()
      if (selectedPeriod) {
        params.append('periodo', selectedPeriod)
      }
      const response = await api.get(`/financial/budgets/${budget.id}/alertas/?${params.toString()}`)
      return response.data
    },
    { enabled: !!budget.id }
  )

  const getSeveridadBadge = (severidad) => {
    const badges = {
      'critica': { bg: 'danger', text: 'Crítica' },
      'alta': { bg: 'warning', text: 'Alta' },
      'media': { bg: 'info', text: 'Media' },
      'baja': { bg: 'secondary', text: 'Baja' }
    }
    const badge = badges[severidad] || { bg: 'secondary', text: severidad }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getTipoAlertaBadge = (tipo) => {
    const badges = {
      'ejecucion': { bg: 'primary', text: 'Ejecución' },
      'desviacion': { bg: 'warning', text: 'Desviación' },
      'comprometido': { bg: 'info', text: 'Comprometido' },
      'vencimiento': { bg: 'danger', text: 'Vencimiento' }
    }
    const badge = badges[tipo] || { bg: 'secondary', text: tipo }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando alertas...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar alertas: {error.message}
      </Alert>
    )
  }

  const alertas = alertsData?.alertas || []
  const estadisticas = alertsData?.estadisticas || {}
  const periodo = alertsData?.periodo || ''

  return (
    <div>
      {/* Filtros */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Período</Form.Label>
                <Form.Control
                  type="month"
                  value={selectedPeriod ? selectedPeriod.substring(0, 7) : ''}
                  onChange={(e) => {
                    const value = e.target.value
                    setSelectedPeriod(value ? value + '-01' : '')
                  }}
                  min={`${budget.año}-01`}
                  max={`${budget.año}-12`}
                />
                <Form.Text className="text-muted">
                  Selecciona un período para ver las alertas. Si no especificas, se mostrarán las del mes actual.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Form.Group>
                <Form.Label className="d-block">Período actual: <strong>{periodo || 'N/A'}</strong></Form.Label>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Estadísticas */}
      {estadisticas && Object.keys(estadisticas).length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Resumen de Alertas</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <div className="text-center">
                  <h3 className="text-danger">{estadisticas.criticas || 0}</h3>
                  <p className="text-muted mb-0">Críticas</p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center">
                  <h3 className="text-warning">{estadisticas.altas || 0}</h3>
                  <p className="text-muted mb-0">Altas</p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center">
                  <h3 className="text-info">{estadisticas.medias || 0}</h3>
                  <p className="text-muted mb-0">Medias</p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center">
                  <h3 className="text-secondary">{alertsData?.total_alertas || 0}</h3>
                  <p className="text-muted mb-0">Total</p>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Lista de alertas */}
      {alertas.length > 0 ? (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Alertas Detalladas</h5>
          </Card.Header>
          <Card.Body>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Severidad</th>
                  <th>Línea</th>
                  <th>Mensaje</th>
                  <th>Detalles</th>
                </tr>
              </thead>
              <tbody>
                {alertas.map((alerta, index) => (
                  <tr key={index}>
                    <td>{getTipoAlertaBadge(alerta.tipo)}</td>
                    <td>{getSeveridadBadge(alerta.severidad)}</td>
                    <td>
                      {alerta.linea_id ? (
                        <Badge bg="secondary">Línea #{alerta.linea_id}</Badge>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>{alerta.mensaje}</td>
                    <td>
                      {alerta.porcentaje !== undefined && (
                        <div>
                          <strong>Porcentaje:</strong> {alerta.porcentaje.toFixed(2)}%
                        </div>
                      )}
                      {alerta.desviacion !== undefined && (
                        <div>
                          <strong>Desviación:</strong> {alerta.desviacion.toFixed(2)}%
                        </div>
                      )}
                      {alerta.monto !== undefined && (
                        <div>
                          <strong>Monto:</strong> {formatCurrency(alerta.monto)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          No hay alertas para este período. El presupuesto está dentro de los parámetros normales.
        </Alert>
      )}
    </div>
  )
}

export default BudgetAlerts
